import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '../../app/hooks.js';
import { selectCatalogQuery, selectSearchText } from '../../store/selectors/index.js';
import { useSearchCatalogQuery } from '../../services/catalogApi.js';
import CatalogFilters from '../catalog/CatalogFilters.js';
import ResultList from '../catalog/ResultList.js';

/**
 * Dataset search.
 *
 * Distinct from Explore: this is the keyword path, Explore is the browse path.
 * Both read the same cached catalog, so switching between them costs nothing.
 */
export default function SearchPanel() {
  const dispatch = useAppDispatch();
  const query = useAppSelector(selectCatalogQuery);
  const searchText = useAppSelector(selectSearchText);
  const { data, isLoading, isError } = useSearchCatalogQuery(query);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <CatalogFilters
        categories={data?.categories ?? []}
        renderableOnly={query.renderableOnly ?? false}
        dispatch={dispatch}
      />

      {isError && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">The dataset catalog could not be loaded.</Alert>
        </Box>
      )}

      {isLoading && (
        <Box sx={{ p: 2 }} aria-busy="true">
          {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} height={56} sx={{ mb: 0.5 }} />)}
        </Box>
      )}

      {!isLoading && !isError && !searchText && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Search {data?.total ?? 0} Oklahoma datasets.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Try &ldquo;county&rdquo;, &ldquo;PLSS&rdquo;, &ldquo;imagery&rdquo;, or &ldquo;watershed&rdquo;.
          </Typography>
        </Box>
      )}

      {!isLoading && !isError && searchText && (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            aria-live="polite"
            sx={{ px: 2, py: 0.75, borderBottom: 1, borderColor: 'divider' }}
          >
            {data?.total ?? 0} matching &ldquo;{searchText}&rdquo;
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ResultList records={data?.records ?? []} />
          </Box>
        </>
      )}
    </Box>
  );
}
